const VISIT_ID_KEY = 'visitID';
const VISIT_ID_MEMORY_KEY = '__tgVisitID';
const INFINITY_INSTALLATION_ID = '3716';
const INFINITY_MASTER_KEY = 'ictf_master';
const INFINITY_LEGACY_KEY = `_its.${INFINITY_INSTALLATION_ID}`;

const isBrowser = () => typeof window !== 'undefined';

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key) || '';
  } catch (err) {
    return '';
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
    return storage.getItem(key) === value;
  } catch (err) {
    return false;
  }
};

const normalizeVisitId = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const setVisitId = (value) => {
  if (!isBrowser()) {
    return '';
  }

  const visitId = normalizeVisitId(value);
  if (!visitId) {
    return '';
  }

  const localSet = safeSet(window.localStorage, VISIT_ID_KEY, visitId);
  const sessionSet = safeSet(window.sessionStorage, VISIT_ID_KEY, visitId);
  window[VISIT_ID_MEMORY_KEY] = visitId;

  return localSet || sessionSet ? visitId : visitId;
};

const setVisitIdOnInput = (visitId, inputId = VISIT_ID_KEY) => {
  if (!isBrowser()) {
    return false;
  }

  const normalized = normalizeVisitId(visitId);
  if (!normalized) {
    return false;
  }

  const input = document.getElementById(inputId);
  if (!input) {
    return false;
  }

  input.value = normalized;
  return true;
};

const parseInfinityInstallationValue = (rawValue) => {
  const normalized = normalizeVisitId(rawValue);
  if (!normalized) {
    return '';
  }

  return normalized.startsWith('vid~')
    ? normalizeVisitId(normalized.split('~')[1] || '')
    : normalized;
};

const getVisitIdFromInfinityTracking = () => {
  if (!isBrowser()) {
    return '';
  }

  try {
    const rawMaster = safeGet(window.localStorage, INFINITY_MASTER_KEY);
    if (rawMaster) {
      const parsedMaster = JSON.parse(rawMaster);
      const masterValue = typeof parsedMaster?.value === 'string'
        ? JSON.parse(parsedMaster.value)
        : parsedMaster?.value;
      const installationValue = parseInfinityInstallationValue(masterValue?.[INFINITY_INSTALLATION_ID]);
      if (installationValue) {
        return installationValue;
      }
    }
  } catch (err) {
    // no-op: fall through to legacy Infinity key parsing
  }

  try {
    const rawLegacy = safeGet(window.localStorage, INFINITY_LEGACY_KEY);
    if (!rawLegacy) {
      return '';
    }

    const parsedLegacy = JSON.parse(rawLegacy);
    if (typeof parsedLegacy?.vid === 'string') {
      return parseInfinityInstallationValue(parsedLegacy.vid);
    }

    return parseInfinityInstallationValue(parsedLegacy?.vid?.value);
  } catch (err) {
    return '';
  }
};

const getVisitId = ({ inputId = VISIT_ID_KEY, syncInput = true } = {}) => {
  if (!isBrowser()) {
    return '';
  }

  const inMemoryVisitId = normalizeVisitId(window[VISIT_ID_MEMORY_KEY] || '');
  if (inMemoryVisitId) {
    if (syncInput) {
      setVisitIdOnInput(inMemoryVisitId, inputId);
    }
    return inMemoryVisitId;
  }

  const infinityVisitId = getVisitIdFromInfinityTracking();
  if (infinityVisitId) {
    setVisitId(infinityVisitId);
    if (syncInput) {
      setVisitIdOnInput(infinityVisitId, inputId);
    }
    return infinityVisitId;
  }

  return '';
};

const startVisitIdSync = ({ inputId = VISIT_ID_KEY, intervalMs = 250, maxAttempts = 40 } = {}) => {
  if (!isBrowser()) {
    return () => {};
  }

  let attempts = 0;
  const sync = () => {
    attempts += 1;
    const visitId = getVisitId({ inputId, syncInput: true });
    if (visitId || attempts >= maxAttempts) {
      clearInterval(intervalId);
    }
  };

  const intervalId = window.setInterval(sync, intervalMs);
  sync();

  return () => clearInterval(intervalId);
};

export {
  VISIT_ID_KEY,
  getVisitId,
  setVisitId,
  setVisitIdOnInput,
  startVisitIdSync,
};
